import * as React from 'react';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import type { EntityWorkflowActionsProps } from '../../types/hooks';
import { usePrimaryAction } from './use-primary-action';
import { useSecondaryAction } from './use-secondary-action';
import { useMenuItems } from './use-menu-items';

export type UseEntityWorkflowActionsResult = ReturnType<
  typeof useEntityWorkflowActions
>;

/**
 * useEntityWorkflowActions - a hub that handles all things entity top bar workflow actions including label, enabled, tooltip, etc. for `EntityStatusValue`
 * @param operations - used in this hook where needed, like to determine which mutate function to call for the save button
 * @param status - used to determine different states and statuses of top bar things. Defaults to New when the status is undefined
 * @param isEntitySaved - based on if the entity is in sync with the data from the GET api call
 * @param entityConfigName - used to construct the tooltip of a menu item
 * @returns top bar workflows to be used by the header container
 */

export const useEntityWorkflowActions = (props: EntityWorkflowActionsProps) => {
  const [isDeepCloneModalOpen, setIsDeepCloneModalOpen] = React.useState(false);
  const [isDeepPublishModalOpen, setIsDeepPublishModalOpen] =
    React.useState(false);

  const entityHasIncludedEntities = (props.includedEntities?.length ?? 0) > 0;
  const isDeepPublishAvailable = isUsingConfigAppBackend({
    entityLevelOverride: props.domainEntity.isUsingConfigAppBackend,
  });

  const primaryAction = usePrimaryAction({
    ...props,
    isDeepPublishAvailable,
    setIsDeepPublishModalOpen,
  });
  const secondaryAction = useSecondaryAction(props);
  const menuItems = useMenuItems({
    ...props,
    setIsDeepCloneModalOpen,
    entityHasIncludedEntities,
  });

  return {
    primaryAction,
    secondaryAction,
    menuItems,
    isDeepCloneModalOpen,
    setIsDeepCloneModalOpen,
    isDeepPublishModalOpen,
    setIsDeepPublishModalOpen,
    entityHasIncludedEntities,
  };
};
